import { ColumnListDto } from '@common/dto/column/list-dto';
import { ColumnSaveDto } from '@common/dto/column/save-dto';
import { EmailTemplateListDto } from '@common/dto/email-template/list';
import { HttpSuccessDto } from '@common/dto/http';
import { InviteStatus } from '@common/dto/invite-status';
import { InviteListDto } from '@common/dto/invite/list-dto';
import { clientTransformed } from '@common/utils/rest';
import client from '@common/utils/rest';
import { executeUI } from '@common/utils/ui-bridge';
import { InviteReviewedStatusStateGetDto } from '@common/dto/invite-reviewed-status-state/get-model';
import { InviteReviewedStatusStateUpdateDto } from '@common/dto/invite-reviewed-status-state/update-dto';
import { InvitePedigreeCreateDto } from '@common/dto/invite/pedigree/create-dto';
import { InviteDatasheetFormatGetDto } from '@common/dto/invite/datasheet/format-get-dto';
import { InviteDatasheetAdditionalDataUpdateDto } from '@common/dto/invite/datasheet/additional-data-update-dto';
import { InviteSendResponseDto } from '@common/dto/invite/send-response-dto';

export const generateLetterFromInvites = (globalId: string | number) => {
  executeUI('INVITES', 'generateLetter', globalId);
};

const URL_PREFIX = 'api/invites';

const getUrl = (path: string) => {
  return `${URL_PREFIX}/${path}`;
};

export const getInvitesColumns = (): Promise<Array<ColumnListDto>> => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'GET'
  }).then(
    (response: HttpSuccessDto<Array<ColumnListDto>>) => {
      return response.entity;
    },
  );
};

export const saveInvitesColumns = (columns: Array<ColumnSaveDto>) => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'POST',
		entity: {
      columns
    },
  });
};


export const restoreInvitesColumnsToDefault = () => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'GET',
    params: {
      default: true
    },
  }).then(
    (response: HttpSuccessDto<Array<ColumnListDto>>) => {
      return response.entity;
    },
  );
};


export type UpdateInviteStatusParams = {
  inviteIds: Array<{ id: number; fhq4: boolean }>;
  status: InviteStatus;
};

export const updateInviteStatus = async (data: UpdateInviteStatusParams) => {
  return clientTransformed({
    path: getUrl('updateInviteStatus'),
    method: 'POST',
		entity: data,
  });
};

export const getReviewedStatusState = (id: InviteListDto["id"], fhq4: boolean) => {
  return clientTransformed({
    path: getUrl('reviewedStatusActions'),
    method: 'GET',
    params: {
      id,
      fhq4,
    },
  }).then(
    (response: HttpSuccessDto<InviteReviewedStatusStateGetDto>) => {
      return response.entity;
    },
  );
};

export const updateReviewedStatusActions = async (data: InviteReviewedStatusStateUpdateDto) => {
  return clientTransformed({
    path: getUrl('reviewedStatusActions'),
    method: 'POST',
		entity: data,
  });
};

export type InviteCreatePedigreeResponse = {
  globalId: number;
  familyId: number;
};

export const createPedigree = (data: InvitePedigreeCreateDto): Promise<InviteCreatePedigreeResponse> => {
  return clientTransformed({
    path: getUrl('createPedigree'),
    method: 'POST',
		entity: data,
  }).then((response: HttpSuccessDto<InviteCreatePedigreeResponse>) => {
    return response.entity;
  });
};

export type InviteGetAdditionalDatasheetFormatDataParams = {
  questionnaireId: number;
  fhq4: boolean;
  globalId: number;
};

export const getAdditionalDatasheetFormatData = (params: InviteGetAdditionalDatasheetFormatDataParams): Promise<InviteDatasheetFormatGetDto> => {
  return clientTransformed({
    path: getUrl('additionalDsData'),
    method: 'GET',
    params: {
      id: params.questionnaireId,
      fhq4: params.fhq4,
      globalId: params.globalId,
    },
  }).then((response: HttpSuccessDto<Array<ColumnListDto>>) => {
    return response.entity;
  });
};

export const saveAdditionalDatasheetFormatData = (data: InviteDatasheetAdditionalDataUpdateDto) => {
  return clientTransformed({
    path: getUrl('saveDsData'),
    method: 'POST',
		entity: data,
  });
};

export type InviteSendParams = {
  questionnaireId: number;
  fhq4: boolean;
  globalIds: Array<number | string>;
  emailSubject?: string;
  emailBody?: string;
};

export const sendInvite = (data: InviteSendParams) => {
  return client({
    path: getUrl('sendBatchInvite'),
    method: 'POST',
    entity: {
      id: data.questionnaireId,
      fhq4: data.fhq4,
      globalIds: data.globalIds,
      emailSubject: data.emailSubject,
      emailBody: data.emailBody,
    },
  }).then((response: { entity: InviteSendResponseDto }) => {
    return response.entity;
  });
};

